<template>
  <div class="home-page">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <van-loading type="spinner" color="#1677ff" size="24px" />
      <p class="loading-text">加载中...</p>
    </div>

    <!-- 页面内容 -->
    <div v-else class="page-content">
      <!-- 用户问候卡片 -->
      <div class="greeting-card">
        <div class="greeting-header">
          <div class="greeting-title-row">
            <h2 class="greeting-title">{{ greetingText }}，{{ (userInfo.user?.nickName?.charAt(0) || '') + '老师' }} 👋</h2>
            <CompanySwitcher :company-ids="userInfo.companyIds || []" />
          </div>
          <p class="greeting-subtitle" v-if="todaySchedules.length > 0 && hasTeacherRole">今天有 {{ todaySchedules.length
          }} 节课，加油！</p>
        </div>
      </div>

      <!-- 数据统计 -->
      <div class="stats-row" v-if="hasTeacherRole">
        <div class="stat-item">
          <div class="stat-value">
            <span>{{ planStats?.todayClassCount || 0 }}</span>
            <span class="stat-unit">节</span>
          </div>
          <div class="stat-label">今日排课</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">
            <span>{{ planStats?.todayBeAttendanceClassCount || 0 }}</span>
            <span class="stat-unit">节</span>
          </div>
          <div class="stat-label">今日待考勤</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">
            <span>{{ planStats?.monthClassCount || 0 }}</span>
            <span class="stat-unit">节</span>
          </div>
          <div class="stat-label">本月累计</div>
        </div>
      </div>

      <!-- 快捷功能 -->
      <div class="quick-actions">
        <div class="section-title">⚡ 快捷功能</div>
        <div class="actions-grid">
          <div v-for="action in quickActions" :key="action.path" class="action-item" @click="goToPage(action.path)">
            <div class="action-icon" :style="{ background: action.color }">
              <van-icon :name="action.icon" size="24" color="#fff" />
            </div>
            <span class="action-name">{{ action.name }}</span>
          </div>
        </div>
      </div>

      <!-- 今日课表 -->
      <div class="today-schedule">
        <div class="section-title">📅 今日课表</div>
        <div v-if="todaySchedules.length > 0" class="schedule-list">
          <div v-for="(schedule, idx) in todaySchedules" :key="idx" class="schedule-item"
            :class="{ completed: schedule.isCompleted }">
            <div class="schedule-info">
              <div class="schedule-time">{{ schedule.startTime }}-{{ schedule.endTime }}</div>
              <div class="schedule-detail">
                <span class="course-name">{{ schedule.courseName }}</span>
                <span class="students">{{ schedule.studentNames }}</span>
                <span class="teacher">{{ schedule.teacherName }}</span>
              </div>
            </div>
            <van-button v-if="!schedule.isCompleted && new Date() > parseDate(schedule.endDateTime)" type="primary"
              size="small" round @click="goToAttendance(schedule.schedulePlanId)">
              去考勤
            </van-button>
            <van-tag v-if="schedule.isCompleted" type="success" plain>已完成</van-tag>
          </div>
        </div>
        <div v-else class="empty-schedule">
          <van-empty description="今天没有课程安排" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: 'Home' })
import { onMounted, onActivated, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { getUserInfo } from '@/api/auth'
import { getTodaySchedules, getTeacherPlanStatics } from '@/api/schedule'
import { setUserInfo } from '@/utils/authUtils'
import { ensureWecomLogin } from '@/utils/wecomAuth'
import { parseDate } from '@/utils/common'
import { initializeCompanyContext } from '@/utils/company'
import CompanySwitcher from '@/components/CompanySwitcher.vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const router = useRouter()
const userInfo = ref({})
const todaySchedules = ref([])
const loading = ref(true)
// 统计数据
const planStats = ref({})

// 根据时间动态显示问候语
const greetingText = computed(() => {
  const hour = new Date().getHours()
  if (hour >= 0 && hour < 6) return '凌晨好'
  if (hour >= 6 && hour < 9) return '早上好'
  if (hour >= 9 && hour < 12) return '上午好'
  if (hour >= 12 && hour < 14) return '中午好'
  if (hour >= 14 && hour < 18) return '下午好'
  return '晚上好'
})



function hasPermission(permission) {
  return userInfo.value?.permissions?.includes(permission) || false
}

const isSingleTeacherRole = computed(() => {
  const roles = userInfo.value?.roles || []
  return roles.length === 1 && roles[0] === 'teacher'
})

const hasTeacherRole = computed(() => {
  const roles = userInfo.value?.roles || []
  return roles.includes('teacher')
})

// 快捷功能配置
const quickActions = computed(() => [
  { name: '订单管理', icon: 'orders-o', path: '/orderList', color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', permission: 'financial:order:list' },
  { name: '线索管理', icon: 'records', path: '/consultationBook', color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', permission: 'enrollment:consultationBook:list' },
  { name: '看板中心', icon: 'chart-trending-o', path: '/boardCenter', color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', permissions: ['enrollment:consultationBook:board', 'financial:performance:board'] },
  { name: '号码库', icon: 'phone-circle-o', path: '/phoneBank', color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', permission: 'enrollment:phoneBank:list' },
  { name: '课表', icon: 'calendar-o', path: '/timeTable', color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', permission: 'educational:schedule:list' },
  { name: '考勤记录', icon: 'passed', path: '/teachingLog', color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', permission: 'educational:schedule:list', hideForTeacher: true },
  { name: '上课记录', icon: 'edit', path: '/myTeachingLog', color: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', permission: 'educational:mySchedule:list' },
  { name: '地推打卡', icon: 'location-o', path: '/checkin', color: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', permission: 'system:checkinRecord:myList' },
].filter(item => {
  if (item.hideForTeacher && isSingleTeacherRole.value) {
    return false
  }
  if (item.permissions) {
    return item.permissions.some(perm => hasPermission(perm))
  }
  return hasPermission(item.permission)
}))

onMounted(async () => {
  try {
    await ensureWecomLogin(route.query.code)
  } catch (error) {
    console.error('企微登录失败:', error)
  }
  await initData()
})

onActivated(() => {
  if (hasPermission('educational:schedule:list')) {
    refreshDataSilently()
  }
})

async function initData() {
  try {
    const userInfoResponse = await getUserInfo()
    userInfo.value = userInfoResponse
    setUserInfo(userInfoResponse)
    initializeCompanyContext(userInfoResponse)
    if (hasPermission('educational:schedule:list')) {
      const schedules = await getTodaySchedules()
      getPlanStats();
      todaySchedules.value = schedules.map(s => ({
        ...s,
        isCompleted: s.signStatus === 2
      }))
    }
  } catch (error) {
    console.error('初始化数据失败:', error)
  } finally {
    loading.value = false
  }
}


async function getPlanStats() {
  try {
    const stats = await getTeacherPlanStatics()
    planStats.value = stats
  } catch (error) {
    console.error('获取课次汇总数据失败:', error)
  }
}

async function refreshDataSilently() {
  try {
    await ensureWecomLogin(route.query.code)
    const schedules = await getTodaySchedules()
    getPlanStats();
    todaySchedules.value = schedules.map(s => ({
      ...s,
      isCompleted: s.signStatus === 2
    }))
  } catch (error) {
    console.error('静默刷新数据失败:', error)
  }
}

function goToPage(path) {
  router.push({ path })
}

function goToAttendance(schedulePlanId) {
  router.push({
    path: '/addAttendance',
    query: { schedulePlanId }
  })
}
</script>

<style scoped>
.home-page {
  background-color: #f5f7fa;
  width: 100%;
  box-sizing: border-box;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f5f7fa;
}

.loading-text {
  margin-top: 12px;
  color: #999;
  font-size: 14px;
}

.page-content {
  padding: 16px;
  padding-bottom: calc(60px + env(safe-area-inset-bottom));
  position: static !important;
  background-color: #f5f7fa;
  width: 100%;
  box-sizing: border-box;
}

/* 问候卡片 */
.greeting-card {
  background: linear-gradient(135deg, #1677ff 0%, #4096ff 100%);
  border-radius: 16px;
  padding: 24px 20px;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(22, 119, 255, 0.25);
}

.greeting-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.greeting-title {
  margin: 0 0 8px 0;
  color: #fff;
  font-size: 20px;
  font-weight: 600;
}

.greeting-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
}

/* 统计行 */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.stat-item {
  background: #fff;
  border-radius: 12px;
  padding: 16px 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.stat-value {
  display: center;
  align-items: baseline;
  gap: 2px;
  font-size: 24px;
  font-weight: 600;
  color: #1677ff;
  line-height: 1;
  margin-bottom: 6px;
}

.stat-unit {
  font-size: 12px;
  font-weight: 400;
  color: #999;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

/* 快捷功能 */
.quick-actions {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 16px;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-item:active {
  transform: scale(0.92);
  opacity: 0.8;
}

.action-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-name {
  font-size: 12px;
  color: #666;
}

/* 今日课表 */
.today-schedule {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.schedule-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.schedule-item.completed {
  background: #f6ffed;
  border-color: #b7eb8f;
}

.schedule-item:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.schedule-info {
  flex: 1;
}

.schedule-time {
  font-size: 14px;
  font-weight: 500;
  color: #262626;
  margin-bottom: 4px;
}

.schedule-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.course-name {
  font-size: 13px;
  color: #666;
}

.students {
  font-size: 12px;
  color: #999;
}

.teacher {
  font-size: 12px;
  color: #999;
}

.empty-schedule {
  padding: 20px 0;
}
</style>
